import { Close, Save } from '@mui/icons-material';
import { Page, PageTitle, Loader, NeighborhoodDropdown, SMBSelect } from '../../../../components';
import { Box, Button, Grid, TextField, MenuItem } from '@mui/material';
import { deepEqual } from 'fast-equals';
import { useQuery } from 'react-query';
import * as Yup from 'yup';
import { useMemo, useState } from 'react';
import { useHistory, Prompt } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Form, Formik } from 'formik';
import {
  createPunchlist,
  getPunchlistTemplates,
  getJobsWithoutPunchlists
} from '../../../../fetch';
import {
  IPunchlistTemplate,
  IResponse,
  IJobWithoutPunchlist,
  INeighborhood
} from '../../../../models';

const NewPunchlistSchema = Yup.object().shape({
  neighborhoodId: Yup.string().required('Required'),
  sageJobId: Yup.string().required('Required'),
  name: Yup.string().required('Required'),
  description: Yup.string(),
  punchlistTemplateId: Yup.string()
});

export const NewPunchlist = () => {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string | number | null>(
    null
  );
  const [neighborhoods, setNeighborhoods] = useState<INeighborhood[]>([]);
  // queries
  const { isLoading: isLoadingTemplates, data: templatesData } = useQuery<
    IResponse<IPunchlistTemplate[]>,
    Error
  >(['new-punchlist-templates'], () =>
    getPunchlistTemplates({
      perPage: '2147483647' // get all records
    })
  );
  const templates = useMemo(() => templatesData?.records ?? [], [templatesData]);
  const { isLoading: isLoadingJobs, data: jobs } = useQuery<IJobWithoutPunchlist[], Error>(
    ['job-dropdown', selectedNeighborhoodId],
    () =>
      getJobsWithoutPunchlists({
        neighborhoodId: selectedNeighborhoodId
      }),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !!selectedNeighborhoodId
    }
  );

  return (
    <Page title="Create Punchlist">
      <PageTitle title="Create Punchlist" />
      <Formik
        enableReinitialize={true}
        initialValues={{
          neighborhoodId: '',
          sageJobId: '',
          name: '',
          description: '',
          punchlistTemplateId: ''
        }}
        validationSchema={NewPunchlistSchema}
        onSubmit={async (values, actions) => {
          try {
            const res = await createPunchlist({
              ...values,
              neighborhoodId: Number(values.neighborhoodId),
              sageJobId: Number(values.sageJobId),
              punchlistTemplateId: values?.punchlistTemplateId
                ? Number(values.punchlistTemplateId)
                : undefined
            });
            enqueueSnackbar('Success, punchlist created!', {
              variant: 'success'
            });
            // reset
            actions.resetForm();
            history.push(`/production/punchlists/${res}`);
          } catch (error) {
            return enqueueSnackbar(error?.Detail || 'Error creating punchlist, please try again.', {
              variant: 'error'
            });
          }
        }}
      >
        {({
          isSubmitting,
          values,
          initialValues,
          setFieldValue,
          handleSubmit,
          dirty,
          handleBlur,
          errors,
          touched
        }) => {
          return (
            <>
              <Prompt
                when={!deepEqual(initialValues, values) ? true : false}
                message="Are you sure you want to navigate away from this page? Your unsaved changes will be lost."
              />
              {isSubmitting && <Loader type="fullscreen" position="centered" />}
              <Form onSubmit={handleSubmit} autoComplete="none">
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={4}>
                    <SMBSelect
                      variant="outlined"
                      disabled={isLoadingTemplates}
                      label={isLoadingTemplates ? 'Loading...' : 'Template'}
                      value={values.punchlistTemplateId}
                      size="small"
                      onChange={e => {
                        setFieldValue('punchlistTemplateId', e.target.value);
                      }}
                      handleClear={() => {
                        setFieldValue('punchlistTemplateId', '');
                      }}
                      onBlur={handleBlur}
                    >
                      {!isLoadingTemplates && templates && templates.length === 0 && (
                        <MenuItem disabled>No Templates available</MenuItem>
                      )}
                      {!isLoadingTemplates &&
                        templates &&
                        templates.map((template, index) => {
                          return (
                            <MenuItem key={`${index}`} value={template.punchlistTemplateId}>
                              {template.name}
                            </MenuItem>
                          );
                        })}
                    </SMBSelect>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <NeighborhoodDropdown
                      isRequired
                      value={values.neighborhoodId}
                      handleChange={value => {
                        setFieldValue('neighborhoodId', value);
                        setSelectedNeighborhoodId(value);
                        if (values.sageJobId) {
                          setFieldValue('sageJobId', '');
                        }
                      }}
                      apiParams={{ isActive: true }}
                      setOptions={records => setNeighborhoods(records)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <SMBSelect
                      variant="outlined"
                      required
                      label={isLoadingJobs ? 'Loading...' : 'Job'}
                      disabled={isLoadingJobs || !selectedNeighborhoodId}
                      value={values.sageJobId}
                      size="small"
                      onChange={({ target: { value } }) => {
                        setFieldValue('sageJobId', value);
                        if (values.neighborhoodId) {
                          const selectedNeighborhood = neighborhoods.find(
                            n => n.neighborhoodId === Number(values.neighborhoodId)
                          );
                          const selectedJob = jobs.find(job => job.sageJobId === Number(value));
                          if (selectedNeighborhood && selectedJob) {
                            setFieldValue(
                              'name',
                              `${selectedNeighborhood.name} Phase ${selectedNeighborhood.phase} Job ${selectedJob.jobDescription} Punchlist`
                            );
                          }
                        }
                      }}
                      handleClear={() => {
                        setFieldValue('sageJobId', '');
                      }}
                    >
                      {!isLoadingJobs && jobs && jobs.length === 0 && (
                        <MenuItem disabled>No Jobs available</MenuItem>
                      )}
                      {!isLoadingJobs &&
                        jobs &&
                        jobs.map((job, index) => {
                          return (
                            <MenuItem key={`${index}`} value={job.sageJobId}>
                              {job.jobDescription}
                            </MenuItem>
                          );
                        })}
                    </SMBSelect>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      autoComplete="nope"
                      label="Name"
                      name="name"
                      value={values.name}
                      onBlur={handleBlur}
                      size="small"
                      onChange={e => setFieldValue('name', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      autoComplete="nope"
                      label="Description"
                      name="description"
                      value={values.description}
                      onBlur={handleBlur}
                      size="small"
                      multiline
                      rows={3}
                      onChange={e => setFieldValue('description', e.target.value)}
                    />
                  </Grid>
                </Grid>
                <Box marginTop="1rem">
                  <Button
                    disabled={
                      !dirty ||
                      isSubmitting ||
                      !values.name ||
                      !values.sageJobId ||
                      !values.neighborhoodId
                    }
                    type="submit"
                    startIcon={<Save />}
                    variant="contained"
                    color="primary"
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    color="inherit"
                    disabled={isSubmitting}
                    startIcon={<Close />}
                    onClick={() => history.push('/production/punchlists/dashboard')}
                  >
                    Cancel
                  </Button>
                </Box>
              </Form>
            </>
          );
        }}
      </Formik>
    </Page>
  );
};
